import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton } from "../components/Primitives";
import { Plus, Maximize2, Minimize2, RefreshCw, ChevronUp, ChevronDown } from "lucide-react";
import { KioskIdleOverlay } from "../components/KioskIdleOverlay";
import { toast } from "sonner";
import { usePermission } from "../contexts/AuthContext";
import {
  MS_PER_DAY,
  SITE_STATUSES,
  SHIFT_STATUSES,
  emptyShift,
  startOfWeek,
  toLocalInput,
  isoDate,
} from "../components/shifts/shiftUtils";
import { RosterFilters } from "../components/shifts/RosterFilters";
import { RosterGrid } from "../components/shifts/RosterGrid";
import { MobileRosterList } from "../components/shifts/MobileRosterList";
import { RosterHeaderActions } from "../components/shifts/RosterHeaderActions";
import { ShiftFormModal } from "../components/shifts/ShiftFormModal";
import { EditShiftModal } from "../components/shifts/EditShiftModal";
import { EditSeriesModal } from "../components/shifts/EditSeriesModal";
import { useRosterView } from "../components/shifts/useRosterView";
import { useRememberWindowGeometry } from "../lib/popoutGeometry";

export default function Shifts() {
  const location = useLocation();
  const canCreateShift = usePermission("shifts:create");
  const fullscreen = location.pathname.includes("/fullscreen");
  useRememberWindowGeometry(fullscreen ? location.pathname : null);
  const [filtersOpen, setFiltersOpen] = useState(!fullscreen);
  const [shifts, setShifts] = useState([]);
  const [guards, setGuards] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyShift);
  const [weeks, setWeeks] = useState(1); // 1, 2, or 4
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [editSeries, setEditSeries] = useState(null);
  const [seriesForm, setSeriesForm] = useState({
    guard_ids: [], original_guard_ids: [], guard_change_mode: "replace_all", guard_change_from: "", guard_change_days: [],
    site_id: "", site_timezone: null, start_hhmm: "", end_hhmm: "", notes: "",
    series_label: "", min_guards: 1, recurrence_days: [], new_start_date: "", new_end_date: "", first_date: "",
  });
  // iter318 — Prevent double-submits on all Roster mutation flows.
  // Users hitting "Update Series" twice was creating duplicate shifts
  // when the underlying request was slow.
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [submittingShift, setSubmittingShift] = useState(false);
  const [submittingSeries, setSubmittingSeries] = useState(false);
  const [submittingDuplicate, setSubmittingDuplicate] = useState(false);
  // iter327 — Configurable short-rest threshold (hours). Fetched once
  // on mount from the agency's alert_settings; defaults to 6h so the
  // Roster keeps its historical behavior if the endpoint fails.
  const [shortRestHours, setShortRestHours] = useState(6);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/agencies/me");
        const val = r.data?.alert_settings?.short_rest_hours;
        if (typeof val === "number") setShortRestHours(val);
      } catch { /* keep default */ }
    })();
  }, []);
  const [editShift, setEditShift] = useState(null);
  const [shiftEditForm, setShiftEditForm] = useState({
    guard_ids: [], site_id: "", start_time: "", end_time: "", notes: "", min_guards: 1,
  });

  // Filters
  const [clientFilter, setClientFilter] = useState([]); // multi-client (empty = all)
  const [siteFilter, setSiteFilter] = useState([]); // multi-site filter (empty = all)
  const [siteStatusFilter, setSiteStatusFilter] = useState(() => new Set(["active"]));
  // Default: hide sites with no shifts in the visible date range. Admins can
  // flip this off from RosterFilters to schedule a brand-new empty site.
  const [hideEmptySites, setHideEmptySites] = useState(() => {
    try { return localStorage.getItem("roster.hideEmptySites") !== "0"; } catch { return true; }
  });
  const [needsCoverageOnly, setNeedsCoverageOnly] = useState(() => {
    try { return localStorage.getItem("roster.needsCoverageOnly") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("roster.hideEmptySites", hideEmptySites ? "1" : "0"); }
    catch (err) {
      // Quota-exceeded / private-browsing mode; the toggle still works in-memory,
      // it just won't survive a reload. Log in dev so we can spot it.
      if (process.env.NODE_ENV === "development") console.debug("Shifts: localStorage setItem skipped:", err?.name);
    }
  }, [hideEmptySites]);
  useEffect(() => {
    try { localStorage.setItem("roster.needsCoverageOnly", needsCoverageOnly ? "1" : "0"); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("Shifts: localStorage setItem skipped:", err?.name);
    }
  }, [needsCoverageOnly]);
  const [shiftStatusFilter, setShiftStatusFilter] = useState(() => new Set(SHIFT_STATUSES.map((s) => s.v)));

  const [loadingMore, setLoadingMore] = useState(false);
  // Track the visible date window so `load()` fetches only the shifts
  // that could render on-screen. Uses `weekStart` + `weeks` to compute
  // an inclusive [start, end] range in YYYY-MM-DD.
  const visibleRange = useMemo(() => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + weeks * 7 - 1);
    // iter324 — Pad the fetch window by 1 day on each side. Prior
    // versions passed the exact visible range, which excluded shifts
    // whose start_time (in UTC) crossed midnight past the end_date.
    // Symptom: last-day shifts at west-of-UTC sites went missing, and
    // first-day shifts at east-of-UTC sites likewise. Sites can be in
    // any timezone from UTC-12 to UTC+14, so 24h padding on each end
    // covers every case. The client-side cellMap keys by browser-local
    // date so extra shifts fetched outside the visible window are
    // silently ignored on render.
    const fetchStart = new Date(start);
    fetchStart.setDate(fetchStart.getDate() - 1);
    const fetchEnd = new Date(end);
    fetchEnd.setDate(fetchEnd.getDate() + 1);
    return {
      start: isoDate(start),
      end: isoDate(end),
      fetchStart: isoDate(fetchStart),
      fetchEnd: isoDate(fetchEnd),
    };
  }, [weekStart, weeks]);

  const load = useCallback(async () => {
    // iter316 — Date-scoped Roster fetch. Instead of pulling every shift
    // on the tenant (which was 10-20s for busy agencies even after
    // pagination), only fetch shifts that overlap the currently visible
    // week window. Renders the first page immediately for fast paint,
    // then continues paginating in the background if a mega-week window
    // still spills past 500 rows (rare — a 1-month view rarely exceeds
    // 500 shifts even for large agencies). Series edit modal fetches
    // the full recurrence_group on-demand via `?recurrence_group=` so
    // its "extend to date" default stays accurate outside the window.
    const params = new URLSearchParams({
      paginated: "1",
      limit: "500",
      start_date: visibleRange.fetchStart,
      end_date: visibleRange.fetchEnd,
    });
    // Kick off guards + sites in parallel with the first shifts page.
    const [firstPage, b, c] = await Promise.all([
      api.get(`/shifts?${params.toString()}`),
      api.get("/guards"),
      api.get("/sites"),
    ]);
    const accumulated = [...(firstPage.data?.items || [])];
    setShifts(accumulated);
    setGuards(b.data);
    setSites(c.data);
    // Follow the cursor for any remaining pages in the background so
    // the grid renders instantly on the first page.
    let cursor = firstPage.data?.next_cursor || null;
    let page = 1;
    const MAX_PAGES = 40;
    if (cursor) setLoadingMore(true);
    while (cursor && page < MAX_PAGES) {
      const p = new URLSearchParams({
        paginated: "1",
        limit: "500",
        start_date: visibleRange.fetchStart,
        end_date: visibleRange.fetchEnd,
        cursor,
      });
      const r = await api.get(`/shifts?${p.toString()}`);
      const items = r.data?.items || [];
      accumulated.push(...items);
      setShifts([...accumulated]);
      cursor = r.data?.next_cursor || null;
      page += 1;
    }
    setLoadingMore(false);
  }, [visibleRange]);
  useEffect(() => { load(); }, [load]);

  // Fullscreen mode: auto-refresh for live ops boards.
  // iter329 — Roster cadence relaxed to 1 hour (was 60s). Shift data
  // rarely changes minute-to-minute so a full pull every 60s was
  // burning cycles for no benefit. Tardies + Open Shifts stay on 60s
  // because their content is time-sensitive.
  useEffect(() => {
    if (!fullscreen) return;
    const id = setInterval(load, 60 * 60 * 1000);
    return () => clearInterval(id);
  }, [fullscreen, load]);

  // Distinct client names
  const clients = useMemo(
    () => Array.from(new Set(sites.map((s) => s.client_name).filter(Boolean))).sort(),
    [sites]
  );

  // Options for MultiSelectDropdown
  const guardOptions = useMemo(() => guards.map((g) => ({
    value: g.id,
    label: g.name,
    sub: g.deactivation_date ? `deact ${g.deactivation_date}` : (g.badge_no || ""),
  })), [guards]);
  const siteOptions = useMemo(() => {
    const cf = new Set(clientFilter);
    return sites
      .filter((s) => cf.size === 0 || cf.has(s.client_name))
      .map((s) => ({ value: s.id, label: s.name, sub: s.client_name }));
  }, [sites, clientFilter]);

  const days = useMemo(() => {
    const total = weeks * 7;
    return Array.from({ length: total }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart, weeks]);

  // Filtered sites (rows) + shifts + cellMap + conflict IDs — extracted
  // into a dedicated hook so this page focuses on state + callbacks. See
  // `components/shifts/useRosterView.js` for the memo logic.
  const {
    visibleSites,
    visibleShifts,
    cellMap,
    conflictIds,
    vacationConflictIds,
    shortRest,
  } = useRosterView({
    shifts,
    sites,
    clientFilter,
    siteFilter,
    siteStatusFilter,
    shiftStatusFilter,
    days,
    hideEmptySites,
    needsCoverageOnly,
    shortRestHours,
  });

  // ── New shift ──────────────────────────────────────────────────
  const openNewShift = (prefill = {}) => {
    // iter312 — Sensible defaults so admins don't stare at an empty
    // start/end pair. Round `now` down to the nearest 5 minutes so the
    // control lands on a clean tick, then add 8h for the default end.
    // Any explicit prefill (drag-select on a roster cell) overrides.
    const now = new Date();
    now.setSeconds(0, 0);
    now.setMinutes(now.getMinutes() - (now.getMinutes() % 5));
    const endDefault = new Date(now.getTime() + 8 * 60 * 60 * 1000);
    setForm({
      ...emptyShift,
      start_time: toLocalInput(now.toISOString()),
      end_time:   toLocalInput(endDefault.toISOString()),
      ...prefill,
    });
    setOpen(true);
  };

  const openNewShiftForCell = (siteId, day) => {
    const startDt = new Date(day); startDt.setHours(8, 0, 0, 0);
    const endDt = new Date(day); endDt.setHours(16, 0, 0, 0);
    openNewShift({
      site_id: siteId,
      start_time: toLocalInput(startDt.toISOString()),
      end_time: toLocalInput(endDt.toISOString()),
    });
  };

  const onSave = async () => {
    if (submittingCreate) return;
    if (!form.site_id || !form.start_time || !form.end_time) {
      toast.error("Site, start & end are required"); return;
    }
    if (form.repeats && (form.recurrence_days.length === 0 || !form.recurrence_end_date)) {
      toast.error("Pick at least one day-of-week and an end date"); return;
    }
    setSubmittingCreate(true);
    try {
      const payload = {
        guard_ids: form.guard_ids,
        site_id: form.site_id,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        notes: form.notes || null,
        min_guards: Math.max(0, Number(form.min_guards) || 0),
      };
      if (form.repeats) {
        payload.recurrence_days = form.recurrence_days;
        payload.recurrence_end_date = form.recurrence_end_date;
        if (form.series_label && form.series_label.trim()) {
          payload.series_label = form.series_label.trim();
        }
      }
      const res = await api.post("/shifts", payload);
      const c = res.data.conflicts || [];
      const openMsg = payload.guard_ids.length === 0 ? " (open)" : "";
      if (c.length) toast.warning(`${res.data.created}${openMsg} scheduled · ${c.length} guard conflict(s)`);
      else toast.success(`${res.data.created} shift${res.data.created > 1 ? "s" : ""}${openMsg} scheduled`);
      setOpen(false); setForm(emptyShift); load();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setSubmittingCreate(false);
    }
  };

  // ── Single shift edit ───────────────────────────────────────────
  const openEditShift = (s) => {
    setEditShift(s);
    setShiftEditForm({
      guard_ids: s.guard_ids && s.guard_ids.length ? [...s.guard_ids] : (s.guard_id ? [s.guard_id] : []),
      site_id: s.site_id,
      start_time: toLocalInput(s.start_time),
      end_time: toLocalInput(s.end_time),
      notes: s.notes || "",
      min_guards: typeof s.min_guards === "number" ? s.min_guards : 1,
    });
  };

  const saveShift = async () => {
    if (submittingShift) return;
    const f = shiftEditForm;
    if (!f.site_id || !f.start_time || !f.end_time) {
      toast.error("Site, start & end are required"); return;
    }
    setSubmittingShift(true);
    try {
      await api.put(`/shifts/${editShift.id}`, {
        guard_ids: f.guard_ids,
        site_id: f.site_id,
        start_time: new Date(f.start_time).toISOString(),
        end_time: new Date(f.end_time).toISOString(),
        notes: f.notes || null,
        min_guards: Math.max(0, Number(f.min_guards) || 0),
      });
      toast.success("Shift updated");
      setEditShift(null); load();
    } catch (e) {
      toast.error(errorMessage(e, "Update failed"));
    } finally {
      setSubmittingShift(false);
    }
  };

  const onDeleteShift = async (id) => {
    if (!window.confirm("Cancel this shift?")) return;
    await api.delete(`/shifts/${id}`);
    toast.success("Cancelled"); setEditShift(null); load();
  };

  const duplicateShift = async (s) => {
    const dflt = isoDate(new Date(new Date(s.start_time).getTime() + 7 * MS_PER_DAY));
    const target = window.prompt("Copy this shift to which date? (YYYY-MM-DD)", dflt);
    if (!target) return;
    try {
      await api.post(`/shifts/${s.id}/duplicate`, { new_date: target });
      toast.success("Shift duplicated");
      setEditShift(null); load();
    } catch (e) {
      toast.error(errorMessage(e, "Duplicate failed"));
    }
  };

  // ── Series ──────────────────────────────────────────────────────
  const openEditSeries = async (s) => {
    setEditSeries({ group_id: s.recurrence_group, sample: s });
    // iter316 — Roster now fetches only the visible date window, so the
    // full recurrence group may not be in local state. Fetch every
    // shift in this series on-demand (indexed by recurrence_group)
    // so the "extend to date" default and the "first_date" hint stay
    // accurate regardless of which week the user is looking at.
    let groupShifts = shifts.filter((x) => x.recurrence_group === s.recurrence_group);
    try {
      const params = new URLSearchParams({
        paginated: "1",
        limit: "500",
        recurrence_group: s.recurrence_group,
      });
      const r = await api.get(`/shifts?${params.toString()}`);
      const full = r.data?.items || [];
      if (full.length) groupShifts = full;
    } catch { /* fall back to whatever is loaded locally */ }
    const lastIso = groupShifts.map((x) => x.start_time).sort().slice(-1)[0] || s.start_time;
    const firstIso = groupShifts.map((x) => x.start_time).sort()[0] || s.start_time;
    const lastDate = new Date(lastIso);
    // iter288 — derive HHMM in the SITE's timezone (not the browser's).
    // Backend interprets these hhmm strings in the site's zone too, so
    // this keeps the roundtrip stable. Falls back to browser local if
    // the site has no timezone configured (matches backend fallback).
    const siteTz = s.site_timezone || null;
    const pad = (n) => String(n).padStart(2, "0");
    const extractHHMM = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      if (siteTz) {
        // Intl gives site-local hours; robust across DST.
        const parts = new Intl.DateTimeFormat("en-GB", {
          timeZone: siteTz,
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).formatToParts(d);
        const h = parts.find((p) => p.type === "hour")?.value || "00";
        const m = parts.find((p) => p.type === "minute")?.value || "00";
        return `${h}:${m}`;
      }
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    // iter318 — surface the existing day-of-week pattern so ops can
    // change it in the modal. Falls back to deriving from the fetched
    // group's site-local weekdays if the series was created before
    // recurrence_days was stored.
    const storedDays = groupShifts.find((x) => Array.isArray(x.recurrence_days) && x.recurrence_days.length)?.recurrence_days;
    const derivedDays = (() => {
      if (Array.isArray(storedDays) && storedDays.length) return [...new Set(storedDays.map((n) => Number(n)))].sort();
      const set = new Set();
      for (const gs of groupShifts) {
        const d = new Date(gs.start_time);
        if (siteTz) {
          const parts = new Intl.DateTimeFormat("en-US", { timeZone: siteTz, weekday: "short" }).format(d);
          const map = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
          if (map[parts] !== undefined) set.add(map[parts]);
        } else {
          set.add(((d.getDay() + 6) % 7)); // JS Sun=0 → Py Mon=0
        }
      }
      return [...set].sort();
    })();
    setSeriesForm({
      guard_ids: s.guard_ids && s.guard_ids.length ? [...s.guard_ids] : (s.guard_id ? [s.guard_id] : []),
      // iter326 — Preserve the initial guard set so we can detect a
      // change on save and expose the "guard change mode" picker only
      // when the user actually swaps someone.
      original_guard_ids: s.guard_ids && s.guard_ids.length ? [...s.guard_ids] : (s.guard_id ? [s.guard_id] : []),
      guard_change_mode: "replace_all",
      guard_change_from: isoDate(new Date()),
      guard_change_days: [],
      site_id: s.site_id,
      site_timezone: siteTz,
      start_hhmm: extractHHMM(s.start_time),
      end_hhmm: extractHHMM(s.end_time),
      notes: s.notes || "",
      series_label: s.series_label || "",
      min_guards: typeof s.min_guards === "number" ? s.min_guards : 1,
      recurrence_days: derivedDays,
      new_start_date: isoDate(new Date(firstIso)),
      new_end_date: isoDate(lastDate),
      first_date: isoDate(new Date(firstIso)),
    });
  };

  const saveSeries = async () => {
    if (submittingSeries) return;
    setSubmittingSeries(true);
    try {
      const body = { ...seriesForm };
      if (typeof body.min_guards === "number") body.min_guards = Math.max(0, body.min_guards);
      // Only send day-of-week + start-date when the user actually
      // changed them, so a save that only touched (say) `notes` doesn't
      // trigger the full reconcile path server-side.
      if (Array.isArray(body.recurrence_days) && body.recurrence_days.length === 0) {
        delete body.recurrence_days;
      }
      // iter326 — Only send guard_change_mode when the guard set has
      // actually changed. Otherwise the backend would spend cycles
      // running the guard-scoping logic against an unchanged list.
      const origSet = new Set(body.original_guard_ids || []);
      const curSet = new Set(body.guard_ids || []);
      const guardChanged = origSet.size !== curSet.size || [...curSet].some((g) => !origSet.has(g));
      delete body.original_guard_ids;
      if (!guardChanged) {
        delete body.guard_change_mode;
        delete body.guard_change_from;
        delete body.guard_change_days;
        delete body.guard_ids;  // don't rewrite when unchanged
      } else if (body.guard_change_mode === "replace_all") {
        // Legacy behavior; no from-date needed.
        delete body.guard_change_from;
      }
      // iter328 — Empty weekday list means "apply on every day the
      // series runs". The backend rejects an empty array explicitly to
      // catch accidental "no days selected" from the UI, so strip it
      // when the user hasn't picked any.
      if (guardChanged && (!Array.isArray(body.guard_change_days) || body.guard_change_days.length === 0)) {
        delete body.guard_change_days;
      }
      const res = await api.put(`/shifts/series/${editSeries.group_id}`, body);
      const parts = [];
      if (res.data.updated) parts.push(`${res.data.updated} updated`);
      if (res.data.added) parts.push(`${res.data.added} added`);
      if (res.data.removed) parts.push(`${res.data.removed} removed`);
      toast.success(parts.join(" · ") || "Series saved");
      setEditSeries(null); load();
    } catch (e) {
      toast.error(errorMessage(e, "Series update failed"));
    } finally {
      setSubmittingSeries(false);
    }
  };

  const onDeleteSeries = async (groupId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete the entire recurring series?")) return;
    const res = await api.delete(`/shifts/series/${groupId}`);
    toast.success(`${res.data.deleted} shifts removed`); load();
  };

  const duplicateSeries = async () => {
    if (submittingDuplicate) return;
    const sample = editSeries.sample;
    const dflt = isoDate(new Date(new Date(sample.start_time).getTime() + 7 * MS_PER_DAY));
    const target = window.prompt("Copy the series — start the new copy on (YYYY-MM-DD)?", dflt);
    if (!target) return;
    setSubmittingDuplicate(true);
    try {
      const res = await api.post(`/shifts/series/${editSeries.group_id}/duplicate`, { new_start_date: target });
      toast.success(`${res.data.created} shift(s) copied`);
      setEditSeries(null); load();
    } catch (e) {
      toast.error(errorMessage(e, "Series duplicate failed"));
    } finally {
      setSubmittingDuplicate(false);
    }
  };

  // ── Drag & drop / move ─────────────────────────────────────────
  const onDropMove = async (e, siteId, dateStr) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("shift-id");
    if (!id) return;
    try {
      const res = await api.post(`/shifts/${id}/move`, { site_id: siteId, new_date: dateStr });
      if (res.data.conflict_with) toast.warning("Moved — conflicts with another shift");
      else toast.success("Shift moved");
      load();
    } catch { toast.error("Move failed"); }
  };

  const moveRange = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7 * weeks);
    setWeekStart(d);
  };

  const openFullscreen = () => {
    window.open("/shifts/fullscreen", "_blank", "noopener");
  };
  const exitFullscreen = () => {
    window.close();
    // window.close() only works if the tab was script-opened. Fallback:
    window.location.href = "/shifts";
  };

  // Fullscreen render — bare layout (no sidebar), collapsible filters, auto-refresh, big roster.
  if (fullscreen) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
        <KioskIdleOverlay enabled label="Iris Roster · Live" />
        <div className="px-6 py-3 border-b border-[#27272A] flex items-center gap-4 bg-[#121212]">
          <div className="font-display text-xl uppercase font-bold tracking-tighter">Iris Security · Roster</div>
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">
            {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} —{" "}
            {days[days.length - 1].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
          <div className="inline-flex border border-[#27272A]" data-testid="weeks-toggle">
            {[{ v: 1, label: "1 Wk" }, { v: 2, label: "2 Wk" }, { v: 4, label: "1 Mo" }].map((opt) => (
              <button key={opt.v} data-testid={`weeks-${opt.v}`} onClick={() => setWeeks(opt.v)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest ${weeks === opt.v ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <div className="inline-flex gap-1">
            <GhostButton onClick={() => moveRange(-1)}>← Prev</GhostButton>
            <GhostButton onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</GhostButton>
            <GhostButton onClick={() => moveRange(1)}>Next →</GhostButton>
          </div>
          <button
            data-testid="toggle-filters"
            onClick={() => setFiltersOpen((v) => !v)}
            className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1"
          >
            {filtersOpen ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
            Filters
          </button>
          <div className="ml-auto inline-flex items-center gap-3">
            {loadingMore && (
              <div className="text-[10px] uppercase tracking-widest text-amber-400 inline-flex items-center gap-1" data-testid="roster-loading-more-pill">
                <RefreshCw className="size-3 animate-spin" /> Loading more shifts…
              </div>
            )}
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1" data-testid="auto-refresh-pill">
              <RefreshCw className="size-3 animate-spin" style={{ animationDuration: "60s" }} /> Live · 60s
            </div>
            <ActionButton data-testid="add-shift-button" onClick={() => openNewShift()} style={!canCreateShift ? { display: "none" } : undefined}>
              <Plus className="size-4" /> New Shift
            </ActionButton>
            <GhostButton data-testid="exit-fullscreen-button" onClick={exitFullscreen}>
              <Minimize2 className="size-3.5" /> Exit
            </GhostButton>
          </div>
        </div>

        {filtersOpen && (
          <div className="px-6 py-3 border-b border-[#27272A]" data-testid="fullscreen-filters">
            <RosterFilters
              clients={clients} siteOptions={siteOptions}
              clientFilter={clientFilter} setClientFilter={setClientFilter}
              siteFilter={siteFilter} setSiteFilter={setSiteFilter}
              siteStatusFilter={siteStatusFilter} setSiteStatusFilter={setSiteStatusFilter}
              shiftStatusFilter={shiftStatusFilter} setShiftStatusFilter={setShiftStatusFilter}
              hideEmptySites={hideEmptySites} setHideEmptySites={setHideEmptySites}
              needsCoverageOnly={needsCoverageOnly} setNeedsCoverageOnly={setNeedsCoverageOnly}
              weekStart={weekStart} days={days} moveRange={moveRange}
              resetToToday={() => setWeekStart(startOfWeek(new Date()))}
            />
          </div>
        )}

        <div className="flex-1 p-4">
          <RosterGrid
            weeks={weeks} days={days}
            visibleSites={visibleSites} totalSitesCount={sites.length}
            cellMap={cellMap} conflictIds={conflictIds} vacationConflictIds={vacationConflictIds} shortRest={shortRest}
            onCellEmptyClick={openNewShiftForCell} onAddToCell={openNewShiftForCell}
            onDropMove={onDropMove}
            onEditShift={openEditShift} onEditSeries={openEditSeries}
            onDeleteSeries={onDeleteSeries} onDeleteShift={onDeleteShift}
            maxHeight={`calc(100vh - ${filtersOpen ? 180 : 80}px)`}
          />
        </div>

        {open && (
          <ShiftFormModal form={form} setForm={setForm} onClose={() => setOpen(false)}
            onSave={onSave} sites={sites} guardOptions={guardOptions} submitting={submittingCreate} />
        )}
        {editShift && (
          <EditShiftModal editShift={editShift} form={shiftEditForm} setForm={setShiftEditForm}
            guards={guards} guardOptions={guardOptions} sites={sites}
            isConflict={conflictIds.has(editShift.id)}
            onClose={() => setEditShift(null)} onSave={saveShift}
            onDelete={onDeleteShift} onDuplicate={duplicateShift} submitting={submittingShift} />
        )}
        {editSeries && (
          <EditSeriesModal form={seriesForm} setForm={setSeriesForm} sites={sites}
            guardOptions={guardOptions} onClose={() => setEditSeries(null)}
            onSave={saveSeries} onDuplicate={duplicateSeries}
            submitting={submittingSeries} submittingDuplicate={submittingDuplicate} />
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Roster"
        subtitle="Sites × days grid. Click any cell to edit or schedule; drag to reschedule; conflicts flagged in red."
        action={
          <RosterHeaderActions
            weeks={weeks}
            onWeeksChange={setWeeks}
            onFullscreen={openFullscreen}
            onNewShift={() => openNewShift()}
            showNewShift={canCreateShift}
          />
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        {loadingMore && (
          <div
            className="inline-flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-1.5 text-[11px] uppercase tracking-widest text-amber-400"
            data-testid="roster-loading-more-banner"
          >
            <RefreshCw className="size-3 animate-spin" /> Loading more shifts…
          </div>
        )}
        <RosterFilters
          clients={clients}
          siteOptions={siteOptions}
          clientFilter={clientFilter}
          setClientFilter={setClientFilter}
          siteFilter={siteFilter}
          setSiteFilter={setSiteFilter}
          siteStatusFilter={siteStatusFilter}
          setSiteStatusFilter={setSiteStatusFilter}
          shiftStatusFilter={shiftStatusFilter}
          setShiftStatusFilter={setShiftStatusFilter}
          hideEmptySites={hideEmptySites}
          setHideEmptySites={setHideEmptySites}
          needsCoverageOnly={needsCoverageOnly}
          setNeedsCoverageOnly={setNeedsCoverageOnly}
          weekStart={weekStart}
          days={days}
          moveRange={moveRange}
          resetToToday={() => setWeekStart(startOfWeek(new Date()))}
        />

        <div className="hidden md:block">
          <RosterGrid
            weeks={weeks}
            days={days}
            visibleSites={visibleSites}
            totalSitesCount={sites.length}
            cellMap={cellMap}
            conflictIds={conflictIds}
            vacationConflictIds={vacationConflictIds}
            shortRest={shortRest}
            onCellEmptyClick={openNewShiftForCell}
            onAddToCell={openNewShiftForCell}
            onDropMove={onDropMove}
            onEditShift={openEditShift}
            onEditSeries={openEditSeries}
            onDeleteSeries={onDeleteSeries}
            onDeleteShift={onDeleteShift}
          />
        </div>
        <MobileRosterList
          days={days}
          visibleSites={visibleSites}
          totalSitesCount={sites.length}
          cellMap={cellMap}
          conflictIds={conflictIds}
          vacationConflictIds={vacationConflictIds}
          shortRest={shortRest}
          onCellEmptyClick={openNewShiftForCell}
          onAddToCell={openNewShiftForCell}
          onEditShift={openEditShift}
          onEditSeries={openEditSeries}
          onDeleteSeries={onDeleteSeries}
          onDeleteShift={onDeleteShift}
        />
      </div>

      {open && (
        <ShiftFormModal
          form={form}
          setForm={setForm}
          onClose={() => setOpen(false)}
          onSave={onSave}
          sites={sites}
          guardOptions={guardOptions}
          submitting={submittingCreate}
        />
      )}

      {editShift && (
        <EditShiftModal
          editShift={editShift}
          form={shiftEditForm}
          setForm={setShiftEditForm}
          guards={guards}
          guardOptions={guardOptions}
          sites={sites}
          isConflict={conflictIds.has(editShift.id)}
          onClose={() => setEditShift(null)}
          onSave={saveShift}
          onDelete={onDeleteShift}
          onDuplicate={duplicateShift}
          submitting={submittingShift}
        />
      )}

      {editSeries && (
        <EditSeriesModal
          form={seriesForm}
          setForm={setSeriesForm}
          sites={sites}
          guardOptions={guardOptions}
          onClose={() => setEditSeries(null)}
          onSave={saveSeries}
          onDuplicate={duplicateSeries}
          submitting={submittingSeries}
          submittingDuplicate={submittingDuplicate}
        />
      )}
    </div>
  );
}
